import { FC, useCallback } from 'react';

import { useLanguageContext } from '~providers';

interface FormattedEndAtProps {
  endAt?: string | null;
}

export const FormattedEndAt: FC<FormattedEndAtProps> = ({ endAt }) => {
  const { locale, lt } = useLanguageContext();

  const formatEndAt = useCallback(
    (endAt?: string | null) => {
      if (!endAt) return '';

      const date = new Date(endAt);

      if (Number.isNaN(date.getTime())) {
        return '';
      }

      const now = new Date();

      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const targetDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      );
      const msPerDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.round(
        (targetDay.getTime() - today.getTime()) / msPerDay,
      );

      if (diffDays === 0) {
        // Today
        const timeString = date.toLocaleTimeString(locale, {
          hour: 'numeric',
          minute: '2-digit',
        });
        return lt(
          'app.rentalPage.newStepThree.swapCharger.endAt.today',
          'Today, {{time}}',
          {
            time: timeString,
          },
        );
      }

      if (diffDays === 1) {
        // Tomorrow
        const timeString = date.toLocaleTimeString(locale, {
          hour: 'numeric',
          minute: '2-digit',
        });
        return lt(
          'app.rentalPage.newStepThree.swapCharger.endAt.tomorrow',
          'Tomorrow, {{time}}',
          {
            time: timeString,
          },
        );
      }

      const getStartOfWeek = (d: Date) => {
        const result = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const day = result.getDay(); // 0 (Sun) - 6 (Sat)
        result.setDate(result.getDate() - day);
        return result;
      };

      const currentWeekStart = getStartOfWeek(now);
      const targetWeekStart = getStartOfWeek(date);

      // Same calendar week as "today" (excluding Today/Tomorrow which are handled above)
      if (currentWeekStart.getTime() === targetWeekStart.getTime()) {
        const weekday = date.toLocaleDateString(locale, {
          weekday: 'long',
        });
        const timeString = date.toLocaleTimeString(locale, {
          hour: 'numeric',
          minute: '2-digit',
        });
        return lt(
          'app.rentalPage.newStepThree.swapCharger.endAt.sameWeek',
          '{{weekday}}, {{time}}',
          {
            weekday,
            time: timeString,
          },
        );
      }

      // Next week or later: localized full date & time, e.g. "Sep 23, 10:10 PM"
      return date.toLocaleString(locale, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
    [locale, lt],
  );

  return <>{formatEndAt(endAt)}</>;
};
